import React, { useMemo } from 'react';
import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import { styled } from '@mui/material/styles';

import {
  selectIsAuthorIconVisible,
  selectLegibleElementColor,
} from '../../StepWrapper/selectors';
import { useStepContext } from '../../StepWrapper/StepWrapper';
import { ArticleCarouselComponent, ArticleComponent } from '../../types';
import { appendAnalyticsParamsToURL } from '../../UtilComponents/Article/Article';
import AgathaMessage from '../AgathaMessage';

interface Props {
  isLastStep: boolean;
  message: string;
  stepIndex: number;
  widgetComponent: ArticleCarouselComponent;
}

const ArticleTextMessage: React.FC<React.PropsWithChildren<Props>> = ({
  message,
  stepIndex,
  widgetComponent,
}) => {
  const context = useStepContext();
  const { customConfigParams, openArticleInWidget, sendTrackingEvent } =
    context;

  const legibleElementColor = selectLegibleElementColor(context);
  const isAuthorIconVisible = selectIsAuthorIconVisible(context, stepIndex);
  const hideNav = customConfigParams?.hide_nav ?? '';
  const {
    article_step_data: articleStepData,
    forethought_text: forethoughtText,
    knowledge_articles: articles,
    read_article_action: readArticleAction,
  } = widgetComponent.component_fields;
  const readArticleText =
    forethoughtText.related_articles_text || 'Related Articles';
  const openInWidget =
    hideNav !== 'true' && readArticleAction === 'expand_in_widget';

  const markdownOptions: MarkdownToJSX.Options = useMemo(
    () => ({
      overrides: {
        a: {
          props: {
            rel: 'noreferrer',
            target: '_blank',
          },
        },
      },
      wrapper: React.Fragment,
    }),
    [],
  );

  function sendArticleTrackingEvent(
    {
      document_id: documentID,
      span_id: spanId,
      standardized_document_id: standardizedDocumentId,
      trace_id: traceId,
    }: ArticleComponent,
    articleIndex: number,
  ) {
    sendTrackingEvent({
      current_step_id: articleStepData?.current_step_id,
      doc_id: documentID,
      doc_index: articleIndex,
      event_type: 'go-to-article',
      intent_workflow_id: articleStepData?.intent_workflow_id,
      intent_workflow_version: articleStepData?.intent_workflow_version,
      org_id: articleStepData?.org_id,
      span_id: spanId,
      standardized_document_id: standardizedDocumentId,
      trace_id: traceId,
    });
  }

  function getLinks() {
    if (
      readArticleAction === 'none' ||
      (!openInWidget && articles.every(article => !article.link))
    ) {
      return null;
    }

    return (
      <>
        <br />
        {readArticleText}:
        <br />
        {articles.map((article, articleIndex) => {
          if (openInWidget) {
            return (
              <ReadArticleLink
                aria-label={`${readArticleText}: ${article.title}`}
                as='button'
                data-testid={`read-article-link-${articleIndex}`}
                key={article.document_id}
                onClick={() => {
                  openArticleInWidget(article, articleIndex);
                  sendArticleTrackingEvent(article, articleIndex);
                }}
                themeColor={legibleElementColor}
              >
                {articleIndex + 1}. {article.title}
              </ReadArticleLink>
            );
          } else if (article.link) {
            return (
              <ReadArticleLink
                aria-label={`${readArticleText}: ${article.title}`}
                data-testid={`read-article-link-${articleIndex}`}
                href={appendAnalyticsParamsToURL(article.link)}
                key={article.document_id}
                onClick={() => {
                  sendArticleTrackingEvent(article, articleIndex);
                }}
                rel='noreferrer'
                target='_blank'
                themeColor={legibleElementColor}
              >
                {articleIndex + 1}. {article.title}
              </ReadArticleLink>
            );
          } else {
            return null;
          }
        })}
      </>
    );
  }

  return (
    <AgathaMessage
      isAuthorIconVisible={isAuthorIconVisible}
      stepIndex={stepIndex}
    >
      <>
        <Markdown options={markdownOptions}>{message}</Markdown>
        {getLinks()}
      </>
    </AgathaMessage>
  );
};

const ReadArticleLink = styled('a')<{ themeColor: string }>`
  color: ${props => props.themeColor};
  font-size: 16px;
  display: block;
  text-align: initial;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
  padding-bottom: 0;
  margin-top: 0;
  background-color: transparent;
  border: none;
`;

export default ArticleTextMessage;
